import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { axiosInstance } from '../../services/api';
import { getUser } from '../../utils/secureStorage';

// ── Accent color schemes ──
const ACCENT = {
  olive: {
    color: 'text-primary-green', border: 'border-primary-green',
    bg: 'bg-green-50', bgSolid: 'bg-primary-green',
  },
  orange: {
    color: 'text-highlight-orange', border: 'border-highlight-orange',
    bg: 'bg-orange-50', bgSolid: 'bg-highlight-orange',
  },
  blue: {
    color: 'text-blue-700', border: 'border-blue-600',
    bg: 'bg-blue-50', bgSolid: 'bg-blue-600',
  },
  teal: {
    color: 'text-teal-700', border: 'border-teal-600',
    bg: 'bg-teal-50', bgSolid: 'bg-teal-600',
  },
};

const CHILD_ACCENT = {
  orange: {
    border: 'border-highlight-orange', bg: 'bg-orange-50',
    avatar: 'bg-highlight-orange', color: 'text-highlight-orange',
    check: 'bg-highlight-orange border-highlight-orange',
  },
  blue: {
    border: 'border-blue-600', bg: 'bg-blue-50',
    avatar: 'bg-child-blue', color: 'text-blue-700',
    check: 'bg-blue-600 border-blue-600',
  },
};

function getActiveAccent(selectedChildIds, children) {
  if (selectedChildIds.length === 0) return ACCENT.orange;
  if (selectedChildIds.length > 1) return ACCENT.teal;
  const child = children.find(c => c.id === selectedChildIds[0]);
  return ACCENT[child?.accentColor ?? 'orange'];
}

function deriveAccent(tier, selectedChildIds, children) {
  if (tier !== 'child') return ACCENT.olive;
  return getActiveAccent(selectedChildIds, children);
}

const getInitials = (child) =>
  [child.first_name, child.last_name].filter(Boolean).map(n => n.charAt(0).toUpperCase()).join('');

const RadioDot = ({ selected, accentBorder, accentBg }) => (
  <div className={`w-[26px] h-[26px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
    selected ? accentBorder : 'border-border-gray'
  } ${selected ? accentBg : 'bg-white'}`}>
    {selected && <div className="w-[9px] h-[9px] rounded-full bg-white" />}
  </div>
);

/**
 * WhosePersonSelector — reusable "Whose [item] is this?" picker
 *
 * Props:
 *   tier: 'mine' | 'child'
 *   onTierChange: (tier) => void
 *   selectedChildIds: string[]
 *   onSelectedChildIdsChange: (ids) => void
 *   children: array (if provided externally, skips fetch)
 *   itemLabel: string (e.g., "milestone", "log") — defaults to "milestone"
 *   userName: string (optional, for the avatar initial)
 */
const WhosePersonSelector = ({
  tier,
  onTierChange,
  selectedChildIds,
  onSelectedChildIdsChange,
  children: externalChildren,
  itemLabel = 'milestone',
  userName,
}) => {
  const [childrenList, setChildrenList] = useState(externalChildren || []);
  const [loading, setLoading] = useState(!externalChildren);

  // Fetch children if not provided externally
  useEffect(() => {
    if (externalChildren) {
      setChildrenList(externalChildren);
      return;
    }
    const fetchChildren = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/genealogy/people/my_family_grouped/');
        const data = response.data;
        const kids = (data.children || []).map((child, idx) => ({
          ...child,
          accentColor: idx % 2 === 0 ? 'orange' : 'blue',
        }));
        setChildrenList(kids);
      } catch (err) {
        console.error('Failed to fetch children:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchChildren();
  }, [externalChildren]);

  const handleToggleChild = (childId) => {
    const next = selectedChildIds.includes(childId)
      ? selectedChildIds.filter(id => id !== childId)
      : [...selectedChildIds, childId];
    onSelectedChildIdsChange(next);
  };

  const handleToggleAll = () => {
    if (selectedChildIds.length === childrenList.length) {
      onSelectedChildIdsChange([]);
    } else {
      onSelectedChildIdsChange(childrenList.map(c => c.id));
    }
  };

  const accentClasses = deriveAccent(tier, selectedChildIds, childrenList);
  const allSelected = childrenList.length > 0 && selectedChildIds.length === childrenList.length;

  // Get user initial for avatar
  const userInitial = userName ? userName.charAt(0).toUpperCase() : (getUser()?.first_name?.charAt(0)?.toUpperCase() || 'N');

  // Banner text
  const getBannerText = () => {
    if (tier === 'mine') return null;
    if (selectedChildIds.length === 0) return null;
    if (selectedChildIds.length === 1) {
      const child = childrenList.find(c => c.id === selectedChildIds[0]);
      const name = child?.first_name || 'your child';
      return `You are writing this as ${name}'s guardian. It will appear in ${name}'s life story chapters.`;
    }
    const names = selectedChildIds
      .map(id => childrenList.find(c => c.id === id)?.first_name || 'child')
      .join(' and ');
    return `This ${itemLabel} will appear in both ${names}'s life story chapters.`;
  };

  // Don't render if no children found and still loading
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="text-sm text-gray-500 text-center">Loading family members...</div>
      </div>
    );
  }

  // If no children, don't show the selector at all
  if (childrenList.length === 0) return null;

  const bannerText = getBannerText();

  return (
    <div className="space-y-3">
      {/* ── Tier 1: My [item] ── */}
      <button
        type="button"
        onClick={() => onTierChange('mine')}
        role="radio"
        aria-checked={tier === 'mine'}
        className={`w-full flex items-center gap-4 border-2 rounded-2xl p-5 cursor-pointer text-left transition-all duration-150 ${
          tier === 'mine'
            ? 'border-primary-green bg-green-50'
            : 'border-border-gray bg-white'
        }`}
      >
        <div className="w-12 h-12 rounded-full bg-primary-green flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          {userInitial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg font-semibold text-gray-900">My {itemLabel}</p>
          <p className="text-base text-gray-500">Saved into my own life story</p>
        </div>
        <RadioDot selected={tier === 'mine'} accentBorder="border-primary-green" accentBg="bg-primary-green" />
      </button>

      {/* ── Tier 2: My child's [item] ── */}
      <div
        role="radio"
        aria-checked={tier === 'child'}
        className={`border-2 rounded-2xl transition-all duration-150 overflow-hidden ${
          tier === 'child'
            ? `${accentClasses.border} ${accentClasses.bg}`
            : 'border-border-gray bg-white'
        }`}
      >
        <button
          type="button"
          onClick={() => onTierChange('child')}
          className="w-full flex items-center gap-4 p-5 cursor-pointer text-left"
        >
          {/* Stacked child avatars */}
          <div className="flex flex-shrink-0">
            {childrenList.map((child, idx) => {
              const isSelected = selectedChildIds.includes(child.id);
              const bgColor = child.accentColor === 'blue' ? 'bg-child-blue' : 'bg-highlight-orange';
              return (
                <div
                  key={child.id}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-white ${bgColor} ${
                    idx > 0 ? '-ml-3' : ''
                  } ${tier === 'child' && !isSelected ? 'opacity-30' : ''} transition-opacity`}
                >
                  {getInitials(child) || '?'}
                </div>
              );
            })}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg font-semibold text-gray-900">My child's {itemLabel}</p>
            <p className="text-base text-gray-500">Saved into their life story — I'm writing it for them</p>
          </div>
          <RadioDot selected={tier === 'child'} accentBorder={accentClasses.border} accentBg={accentClasses.bgSolid} />
        </button>

        {/* ── Expanded child list ── */}
        {tier === 'child' && childrenList.length > 0 && (
          <div className="pt-4 mt-0 mx-5 mb-5 border-t-2 border-opacity-30 border-current" style={{ borderColor: 'inherit' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-semibold text-gray-600">Which children?</span>
              {childrenList.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleToggleAll(); }}
                  className={`px-4 py-2 rounded-full border-2 text-sm font-semibold cursor-pointer transition-colors ${
                    allSelected
                      ? `${accentClasses.border} ${accentClasses.bg} ${accentClasses.color}`
                      : 'border-gray-300 bg-white text-gray-500'
                  }`}
                >
                  {allSelected ? '\u2713 All selected' : 'Select all'}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {childrenList.map((child) => {
                const checked = selectedChildIds.includes(child.id);
                const initials = getInitials(child);
                const childAccent = CHILD_ACCENT[child.accentColor] || CHILD_ACCENT.orange;
                return (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => handleToggleChild(child.id)}
                    role="checkbox"
                    aria-checked={checked}
                    className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border-[2.5px] cursor-pointer transition-all duration-150 min-h-[56px] ${
                      checked
                        ? `${childAccent.border} ${childAccent.bg} shadow-sm`
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {child.profile_photo ? (
                      <img src={child.profile_photo} alt={child.first_name} className={`w-11 h-11 rounded-full object-cover flex-shrink-0`} />
                    ) : (
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${childAccent.avatar}`}>
                        {initials || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-lg font-semibold truncate ${checked ? childAccent.color : 'text-gray-900'}`}>
                        {child.first_name} {child.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {checked ? 'Will appear in their life story' : 'Not included'}
                      </p>
                    </div>
                    <div className={`w-[26px] h-[26px] rounded-lg border-[3px] flex items-center justify-center flex-shrink-0 transition-colors ${
                      checked ? childAccent.check : 'border-gray-300 bg-white'
                    }`}>
                      {checked && <Check size={14} className="text-white" strokeWidth={3} />}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedChildIds.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-sm text-yellow-800 mt-3">
                Please select at least one child to continue
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Guardian banner ── */}
      {bannerText && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 flex items-center gap-3">
          <span className="text-xl">👶</span>
          <p className="text-sm text-gray-700">{bannerText}</p>
        </div>
      )}
    </div>
  );
};

export default WhosePersonSelector;
